import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ImageSearchOutlinedIcon from "@mui/icons-material/ImageSearchOutlined";
import {
  CSSProperties,
  DragEvent,
  PointerEvent,
  ReactNode,
  SyntheticEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useDebouncedCallback } from "use-debounce";
import {
  selectSelectedElement,
  updateSelectedInteraction,
} from "../../../app/slices/TrainingSlice";
import { AssetDetailsDTO, AssetKey } from "../../../dto/AssetDTO";
import { GetNewGuid } from "../../../helpers/GuidHelper";
import { useAssetPreview } from "../../../hooks/AssetPreviewHook";
import {
  IMAGE_DRAG_AND_DROP_QUIZ_MAX_IMAGES,
  IImageDragAndDropQuiz,
  IImageDragAndDropQuizDropZone,
  IImageDragAndDropQuizImage,
} from "../../../models/interactions/ImageDragAndDropQuiz";
import FileExplorerDialog from "../../file_explorer/FileExplorerDialog";
import PiBasicQuizAddAnswerButtonV2 from "../components/v2/PiBasicQuizAddAnswerButtonV2";
import "./ImageDragAndDropQuizPreviewBody.css";

type ActiveZoneDrag = {
  mode: "move" | "resize";
  pointerId: number;
  startClientX: number;
  startClientY: number;
  zone: IImageDragAndDropQuizDropZone;
};

type ImageOverlayBounds = {
  left: number;
  top: number;
  width: number;
  height: number;
};

const IMAGE_DRAG_TYPE = "application/x-pi-image-dnd-id";
const MAX_ZONES = 15;

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const getZoneLetter = (index: number) => {
  let value = index + 1;
  let result = "";

  while (value > 0) {
    value -= 1;
    result = String.fromCharCode(65 + (value % 26)) + result;
    value = Math.floor(value / 26);
  }

  return result;
};

const createDefaultDropZone = (
  correctImageId?: string,
): IImageDragAndDropQuizDropZone => ({
  id: GetNewGuid(),
  correctImageId,
  xPos: 0.5,
  yPos: 0.5,
  xSize: 0.18,
  ySize: 0.14,
});

function Section(props: {
  title: string;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
}) {
  return (
    <div
      className={
        "flex flex-col flex-1 min-h-0 gap-[10px] " + (props.className ?? "")
      }
    >
      <div className="text-[14px] font-semibold text-interaction-primary">
        {props.title}
      </div>
      <div
        className={
          "flex flex-col min-h-0 gap-[8px] " + (props.bodyClassName ?? "")
        }
      >
        {props.children}
      </div>
    </div>
  );
}

function AssetThumbnail(props: {
  assetId: string;
  alt: string;
  className?: string;
  emptyClassName?: string;
  imageClassName?: string;
  showEditOverlay?: boolean;
  onClick?: () => void;
}) {
  const assetPreview = useAssetPreview(props.assetId);
  const url = assetPreview?.getAssetPreview() ?? "";

  if (url) {
    return (
      <button
        aria-label={props.alt}
        className={
          props.className ??
          "relative flex w-full h-full overflow-hidden rounded-[5px] group"
        }
        onClick={props.onClick}
        type="button"
      >
        <img
          alt={props.alt}
          className={
            props.imageClassName ??
            "object-contain max-w-full max-h-full m-auto group-hover:opacity-60"
          }
          src={url}
        />
        {props.showEditOverlay && (
          <EditOutlinedIcon
            className="absolute invisible -translate-x-1/2 -translate-y-1/2 text-interaction-primary group-hover:visible top-1/2 left-1/2"
            fontSize="small"
          />
        )}
      </button>
    );
  }

  return (
    <button
      aria-label={props.alt}
      className={
        props.emptyClassName ??
        "flex items-center justify-center w-full h-full rounded-[5px] text-interaction-primary hover:bg-interaction-primary-washed"
      }
      onClick={props.onClick}
      type="button"
    >
      <AddPhotoAlternateOutlinedIcon fontSize="small" />
    </button>
  );
}

function ImageChoice(props: {
  image: IImageDragAndDropQuizImage;
  index: number;
  assignedZoneIndex: number;
  isPlacing: boolean;
  canDelete: boolean;
  onBeginPlacement: () => void;
  onClearAssignment: () => void;
  onDelete: () => void;
  onDragEnd: () => void;
  onDragStart: (
    event: DragEvent<HTMLDivElement>,
    imageId: string,
  ) => void;
  onSelectAsset: (imageId: string, assetId: string) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const assignedZoneLetter =
    props.assignedZoneIndex >= 0
      ? getZoneLetter(props.assignedZoneIndex)
      : undefined;

  const onAssetSelect = (asset: AssetDetailsDTO) => {
    props.onSelectAsset(props.image.id, asset.assetId);
    setIsOpen(false);
  };

  const isAssigned = assignedZoneLetter !== undefined;

  return (
    <>
      <div
        className={
          "imagedndquizpreview-image-card relative flex flex-col w-full rounded-[8px] border border-solid bg-white dark:bg-creator-surface overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_10px_rgba(0,0,0,0.12)] transition-shadow " +
          (props.isPlacing
            ? "border-interaction-primary ring-2 ring-interaction-primary/40 "
            : "border-interaction-primary-washed dark:border-creator-border ")
        }
        data-testid={`image-dnd-image-card-${props.image.id}`}
      >
        <div
          className="imagedndquizpreview-image-thumbnail relative w-full aspect-square overflow-hidden bg-[linear-gradient(135deg,var(--pi-game-blue-faded)_0%,rgba(255,255,255,0)_70%)] dark:bg-creator-surface"
          data-testid={`image-dnd-image-thumbnail-${props.image.id}`}
        >
          <AssetThumbnail
            alt={`Select image ${props.index + 1}`}
            assetId={props.image.assetId}
            className="relative flex w-full h-full overflow-hidden group"
            emptyClassName="flex flex-col items-center justify-center w-full h-full gap-[6px] text-interaction-primary hover:bg-interaction-primary-washed/60"
            imageClassName="object-contain max-w-full max-h-full m-auto p-[4px] group-hover:opacity-60"
            onClick={() => setIsOpen(true)}
            showEditOverlay
          />

        </div>

        <div className="imagedndquizpreview-card-footer flex items-center gap-[8px] px-[10px] py-[8px] border-t border-solid border-interaction-primary-washed dark:border-creator-border bg-white dark:bg-creator-surface">
          <div
            aria-label={
              props.isPlacing
                ? `Cancel placement for image ${props.index + 1}`
                : `Drag image ${props.index + 1} onto a zone`
            }
            className={
              "flex items-center justify-center w-[28px] h-[28px] rounded-full select-none cursor-grab active:cursor-grabbing bg-interaction-primary text-white hover:bg-interaction-primary-hover shrink-0 " +
              (props.isPlacing ? "ring-2 ring-offset-2 ring-interaction-primary " : "")
            }
            data-testid={`image-dnd-image-handle-${props.image.id}`}
            draggable
            onClick={props.onBeginPlacement}
            onDragEnd={props.onDragEnd}
            onDragStart={(event) => props.onDragStart(event, props.image.id)}
            role="button"
            title={
              props.isPlacing
                ? "Click a zone to assign, or press Esc to cancel"
                : "Drag onto a zone"
            }
          >
            <DragIndicatorIcon fontSize="small" />
          </div>
          {isAssigned ? (
            <button
              aria-label={`Unassign image ${props.index + 1} from zone ${assignedZoneLetter}`}
              className="flex flex-shrink-0 items-center gap-[2px] h-[24px] pl-[8px] pr-[4px] rounded-full bg-interaction-primary text-white text-[11px] font-semibold hover:bg-interaction-primary-hover"
              onClick={props.onClearAssignment}
              type="button"
            >
              <span>{assignedZoneLetter}</span>
              <CloseIcon style={{ fontSize: 14 }} />
            </button>
          ) : (
            <div
              aria-hidden
              className="imagedndquizpreview-unassigned-chip flex flex-shrink-0 items-center h-[24px] px-[8px] rounded-full text-[11px] font-medium text-interaction-primary opacity-70 border border-dashed border-interaction-primary"
            >
              Drag to image
            </div>
          )}
          <div className="flex-1" />
          <button
            aria-label="Delete image"
            className="flex items-center justify-center w-[28px] h-[28px] rounded-full text-interaction-primary hover:bg-interaction-primary-washed disabled:text-interaction-disabled disabled:cursor-not-allowed shrink-0"
            disabled={!props.canDelete}
            onClick={props.onDelete}
            type="button"
          >
            <DeleteOutlineIcon fontSize="small" />
          </button>
        </div>
      </div>

      <FileExplorerDialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        onSelect={onAssetSelect}
        type={AssetKey.Image}
      />
    </>
  );
}

function AssignedZoneImage(props: {
  assetId: string;
  zoneId: string;
  onAspectRatioChange: (aspectRatio: number) => void;
}) {
  const assetPreview = useAssetPreview(props.assetId);
  const url = assetPreview?.getAssetPreview() ?? "";

  return (
    <div
      className="absolute inset-0 z-[1] flex overflow-hidden rounded-[5px]"
      data-testid={`image-dnd-zone-image-${props.zoneId}`}
    >
      <img
        alt="Assigned image"
        className="w-full h-full object-cover pointer-events-none"
        onLoad={(event) => {
          const image = event.currentTarget;
          if (!image.naturalWidth || !image.naturalHeight) return;
          props.onAspectRatioChange(image.naturalWidth / image.naturalHeight);
        }}
        src={url}
      />
    </div>
  );
}

export default function ImageDragAndDropQuizPreviewBody() {
  const quiz = useSelector(selectSelectedElement) as IImageDragAndDropQuiz;
  const dispatch = useDispatch();
  const assetPreview = useAssetPreview(quiz.imageAssetId);
  const previewUrl = assetPreview?.getAssetPreview() ?? "";
  const imageStageRef = useRef<HTMLDivElement | null>(null);
  const imageOverlayRef = useRef<HTMLDivElement | null>(null);
  const backgroundImageRef = useRef<HTMLImageElement | null>(null);
  const [fileExplorerOpen, setFileExplorerOpen] = useState(false);
  const [activeDrag, setActiveDrag] = useState<ActiveZoneDrag | null>(null);
  const [selectedZoneId, setSelectedZoneId] = useState<string | undefined>(
    quiz.dropZones[0]?.id,
  );
  const [placementImageId, setPlacementImageId] = useState<string | undefined>(
    undefined,
  );
  const [dragOverZoneId, setDragOverZoneId] = useState<string | undefined>(
    undefined,
  );
  const [localZoneOverride, setLocalZoneOverride] =
    useState<IImageDragAndDropQuizDropZone | null>(null);
  const [imageAspectRatios, setImageAspectRatios] = useState<
    Record<string, number>
  >({});
  const [backgroundAspectRatio, setBackgroundAspectRatio] = useState<
    number | undefined
  >(undefined);
  const [imageOverlayBounds, setImageOverlayBounds] = useState<
    ImageOverlayBounds | undefined
  >(undefined);

  const selectedZone =
    quiz.dropZones.find((dropZone) => dropZone.id === selectedZoneId) ??
    quiz.dropZones[0];
  const canAddImage = quiz.images.length < IMAGE_DRAG_AND_DROP_QUIZ_MAX_IMAGES;

  const measureImageOverlayBounds = useCallback(() => {
    const stage = imageStageRef.current;
    const image = backgroundImageRef.current;
    if (!stage || !image) return;

    const stageBounds = stage.getBoundingClientRect();
    const imageBounds = image.getBoundingClientRect();
    if (!stageBounds.width || !stageBounds.height || !imageBounds.width || !imageBounds.height) {
      return;
    }

    const nextBounds = {
      left: imageBounds.left - stageBounds.left,
      top: imageBounds.top - stageBounds.top,
      width: imageBounds.width,
      height: imageBounds.height,
    };

    setImageOverlayBounds((previousBounds) =>
      previousBounds &&
      previousBounds.left === nextBounds.left &&
      previousBounds.top === nextBounds.top &&
      previousBounds.width === nextBounds.width &&
      previousBounds.height === nextBounds.height
        ? previousBounds
        : nextBounds,
    );
  }, []);

  useEffect(() => {
    if (!placementImageId) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setPlacementImageId(undefined);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [placementImageId]);

  useEffect(() => {
    if (!quiz.imageAssetId) {
      setImageOverlayBounds(undefined);
      return;
    }

    measureImageOverlayBounds();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measureImageOverlayBounds);
      return () =>
        window.removeEventListener("resize", measureImageOverlayBounds);
    }

    const resizeObserver = new ResizeObserver(measureImageOverlayBounds);
    if (imageStageRef.current) resizeObserver.observe(imageStageRef.current);
    if (backgroundImageRef.current) {
      resizeObserver.observe(backgroundImageRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [measureImageOverlayBounds, previewUrl, quiz.imageAssetId]);

  const updateQuiz = (updatedQuiz: IImageDragAndDropQuiz) => {
    dispatch(updateSelectedInteraction(updatedQuiz));
  };

  const updateDropZones = (dropZones: IImageDragAndDropQuizDropZone[]) => {
    updateQuiz({ ...quiz, dropZones });
  };

  const updateDropZone = (dropZone: IImageDragAndDropQuizDropZone) => {
    updateDropZones(
      quiz.dropZones.map((existingDropZone) =>
        existingDropZone.id === dropZone.id ? dropZone : existingDropZone,
      ),
    );
  };

  const debouncedCommitDropZone = useDebouncedCallback(
    (zone: IImageDragAndDropQuizDropZone) => updateDropZone(zone),
    100,
  );

  const constrainZone = (
    zone: IImageDragAndDropQuizDropZone,
  ): IImageDragAndDropQuizDropZone => {
    const xSize = clamp(zone.xSize, 0.04, 1);
    const ySize = clamp(zone.ySize, 0.04, 1);

    return {
      ...zone,
      xSize,
      ySize,
      xPos: clamp(zone.xPos, xSize / 2, 1 - xSize / 2),
      yPos: clamp(zone.yPos, ySize / 2, 1 - ySize / 2),
    };
  };

  const fitZoneToImageAspect = (
    zone: IImageDragAndDropQuizDropZone,
    imageAspectRatio: number | undefined,
    preferredDimension: "width" | "height" = "width",
    sourceAspectRatio = backgroundAspectRatio,
  ): IImageDragAndDropQuizDropZone => {
    const bounds = imageOverlayRef.current?.getBoundingClientRect();
    const measuredSourceAspectRatio =
      imageOverlayBounds?.width && imageOverlayBounds.height
        ? imageOverlayBounds.width / imageOverlayBounds.height
        : undefined;
    const normalizedSourceAspectRatio =
      sourceAspectRatio &&
      Number.isFinite(sourceAspectRatio) &&
      sourceAspectRatio > 0
        ? sourceAspectRatio
        : undefined;
    if (
      !imageAspectRatio ||
      !Number.isFinite(imageAspectRatio) ||
      (!measuredSourceAspectRatio &&
        !normalizedSourceAspectRatio &&
        (!bounds?.width || !bounds?.height))
    ) {
      return constrainZone(zone);
    }

    const fallbackOverlayAspectRatio =
      bounds && bounds.width && bounds.height
        ? bounds.width / bounds.height
        : undefined;
    const overlayAspectRatio =
      measuredSourceAspectRatio ??
      fallbackOverlayAspectRatio ??
      normalizedSourceAspectRatio;
    if (!overlayAspectRatio) return constrainZone(zone);

    let xSize = clamp(zone.xSize, 0.04, 1);
    let ySize = clamp(zone.ySize, 0.04, 1);

    if (preferredDimension === "height") {
      xSize = (imageAspectRatio * ySize) / overlayAspectRatio;
      if (xSize > 1) {
        xSize = 1;
        ySize = overlayAspectRatio / imageAspectRatio;
      }
      if (xSize < 0.04) {
        xSize = 0.04;
        ySize = (xSize * overlayAspectRatio) / imageAspectRatio;
      }
    } else {
      ySize = (xSize * overlayAspectRatio) / imageAspectRatio;
      if (ySize > 1) {
        ySize = 1;
        xSize = imageAspectRatio / overlayAspectRatio;
      }
      if (ySize < 0.04) {
        ySize = 0.04;
        xSize = (imageAspectRatio * ySize) / overlayAspectRatio;
      }
    }

    return constrainZone({
      ...zone,
      xSize,
      ySize,
    });
  };

  const assignImageToZone = (imageId: string, dropZoneId: string) => {
    updateDropZones(
      quiz.dropZones.map((existingDropZone) => {
        const updatedDropZone = {
          ...existingDropZone,
          correctImageId:
            existingDropZone.id === dropZoneId
              ? imageId
              : existingDropZone.correctImageId === imageId
                ? undefined
                : existingDropZone.correctImageId,
        };

        return existingDropZone.id === dropZoneId
          ? fitZoneToImageAspect(updatedDropZone, imageAspectRatios[imageId])
          : updatedDropZone;
      }),
    );
    setSelectedZoneId(dropZoneId);
  };

  const clearZoneAssignment = (dropZoneId: string) => {
    updateDropZones(
      quiz.dropZones.map((existingDropZone) =>
        existingDropZone.id === dropZoneId
          ? { ...existingDropZone, correctImageId: undefined }
          : existingDropZone,
      ),
    );
  };

  const addImage = () => {
    if (quiz.images.length >= IMAGE_DRAG_AND_DROP_QUIZ_MAX_IMAGES) return;

    updateQuiz({
      ...quiz,
      images: [...quiz.images, { id: GetNewGuid(), assetId: "" }],
    });
  };

  const updateImageAsset = (imageId: string, assetId: string) => {
    setImageAspectRatios(({ [imageId]: _oldAspectRatio, ...rest }) => rest);
    updateQuiz({
      ...quiz,
      images: quiz.images.map((image) =>
        image.id === imageId ? { ...image, assetId } : image,
      ),
    });
  };

  const deleteImage = (imageId: string) => {
    const images = quiz.images.filter((image) => image.id !== imageId);
    const dropZones = quiz.dropZones.map((dropZone) => ({
      ...dropZone,
      correctImageId:
        dropZone.correctImageId === imageId
          ? undefined
          : dropZone.correctImageId,
    }));

    if (placementImageId === imageId) setPlacementImageId(undefined);
    updateQuiz({ ...quiz, images, dropZones });
  };

  const onSelectImage = (asset: AssetDetailsDTO) => {
    updateQuiz({ ...quiz, imageAssetId: asset.assetId });
    setFileExplorerOpen(false);
  };

  const onBackgroundImageLoad = (event: SyntheticEvent<HTMLImageElement>) => {
    const image = event.currentTarget;
    if (!image.naturalWidth || !image.naturalHeight) return;

    setBackgroundAspectRatio(image.naturalWidth / image.naturalHeight);
    measureImageOverlayBounds();
  };

  const addDropZone = () => {
    const dropZone = createDefaultDropZone();

    updateQuiz({
      ...quiz,
      dropZones: [...quiz.dropZones, dropZone],
    });
    setSelectedZoneId(dropZone.id);
  };

  const deleteDropZone = (dropZoneId: string) => {
    const dropZones = quiz.dropZones.filter(
      (dropZone) => dropZone.id !== dropZoneId,
    );

    updateQuiz({ ...quiz, dropZones });
    if (selectedZoneId === dropZoneId) {
      setSelectedZoneId(dropZones[0]?.id);
    }
  };

  const updateActiveZoneFromPointer = (
    event: PointerEvent<HTMLDivElement>,
    drag: ActiveZoneDrag,
  ) => {
    const bounds = imageOverlayRef.current?.getBoundingClientRect();
    if (!bounds?.width || !bounds?.height) return;

    const xDiff = (event.clientX - drag.startClientX) / bounds.width;
    const yDiff = (event.clientY - drag.startClientY) / bounds.height;
    const updatedZone =
      drag.mode === "move"
        ? {
            ...drag.zone,
            xPos: drag.zone.xPos + xDiff,
            yPos: drag.zone.yPos + yDiff,
          }
        : {
            ...drag.zone,
            xSize: drag.zone.xSize + xDiff,
            ySize: drag.zone.ySize + yDiff,
          };

    const imageAspectRatio = drag.zone.correctImageId
      ? imageAspectRatios[drag.zone.correctImageId]
      : undefined;
    const constrained =
      drag.mode === "resize" && imageAspectRatio
        ? fitZoneToImageAspect(
            updatedZone,
            imageAspectRatio,
            Math.abs(yDiff) > Math.abs(xDiff) ? "height" : "width",
          )
        : constrainZone(updatedZone);
    setLocalZoneOverride(constrained);
    debouncedCommitDropZone(constrained);
  };

  const onZonePointerDown = (
    event: PointerEvent<HTMLDivElement>,
    zone: IImageDragAndDropQuizDropZone,
    mode: ActiveZoneDrag["mode"],
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setSelectedZoneId(zone.id);

    if (placementImageId && mode === "move") {
      assignImageToZone(placementImageId, zone.id);
      setPlacementImageId(undefined);
      return;
    }

    event.currentTarget.setPointerCapture(event.pointerId);
    setActiveDrag({
      mode,
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      zone,
    });
  };

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (!activeDrag || activeDrag.pointerId !== event.pointerId) return;
    updateActiveZoneFromPointer(event, activeDrag);
  };

  const onPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    if (!activeDrag || activeDrag.pointerId !== event.pointerId) return;
    setActiveDrag(null);
    debouncedCommitDropZone.flush();
    setLocalZoneOverride(null);
  };

  const onImageDragStart = (
    event: DragEvent<HTMLDivElement>,
    imageId: string,
  ) => {
    event.dataTransfer.setData(IMAGE_DRAG_TYPE, imageId);
    event.dataTransfer.effectAllowed = "move";
  };

  const onImageDragEnd = () => {
    setDragOverZoneId(undefined);
  };

  const onZoneDragOver = (
    event: DragEvent<HTMLDivElement>,
    dropZone: IImageDragAndDropQuizDropZone,
  ) => {
    if (!event.dataTransfer.types.includes(IMAGE_DRAG_TYPE)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    if (dragOverZoneId !== dropZone.id) setDragOverZoneId(dropZone.id);
  };

  const onZoneDragLeave = (dropZone: IImageDragAndDropQuizDropZone) => {
    if (dragOverZoneId === dropZone.id) setDragOverZoneId(undefined);
  };

  const onZoneDrop = (
    event: DragEvent<HTMLDivElement>,
    dropZone: IImageDragAndDropQuizDropZone,
  ) => {
    event.preventDefault();
    const imageId = event.dataTransfer.getData(IMAGE_DRAG_TYPE);
    setDragOverZoneId(undefined);
    if (!imageId) return;
    assignImageToZone(imageId, dropZone.id);
  };

  const onImageAspectRatioChange = (
    imageId: string,
    aspectRatio: number,
  ) => {
    if (!Number.isFinite(aspectRatio) || aspectRatio <= 0) return;

    setImageAspectRatios((previousRatios) =>
      previousRatios[imageId] === aspectRatio
        ? previousRatios
        : { ...previousRatios, [imageId]: aspectRatio },
    );
  };

  const renderImage = (image: IImageDragAndDropQuizImage, index: number) => {
    const assignedZoneIndex = quiz.dropZones.findIndex(
      (dropZone) => dropZone.correctImageId === image.id,
    );
    const assignedZone =
      assignedZoneIndex >= 0 ? quiz.dropZones[assignedZoneIndex] : undefined;
    const isPlacing = placementImageId === image.id;

    return (
      <ImageChoice
        assignedZoneIndex={assignedZoneIndex}
        canDelete={quiz.images.length > 1}
        image={image}
        index={index}
        isPlacing={isPlacing}
        key={image.id}
        onBeginPlacement={() =>
          setPlacementImageId(isPlacing ? undefined : image.id)
        }
        onClearAssignment={() =>
          assignedZone && clearZoneAssignment(assignedZone.id)
        }
        onDelete={() => deleteImage(image.id)}
        onDragEnd={onImageDragEnd}
        onDragStart={onImageDragStart}
        onSelectAsset={updateImageAsset}
      />
    );
  };

  const renderZone = (
    dropZone: IImageDragAndDropQuizDropZone,
    index: number,
  ) => {
    const effectiveZone =
      localZoneOverride && localZoneOverride.id === dropZone.id
        ? localZoneOverride
        : dropZone;
    const image = quiz.images.find(
      (item) => item.id === dropZone.correctImageId,
    );
    const isSelected = selectedZone?.id === dropZone.id;
    const isDropTarget = dragOverZoneId === dropZone.id;
    const zoneLetter = getZoneLetter(index);

    return (
      <div
        className={
          "absolute flex items-center justify-center rounded-[5px] text-white cursor-grab touch-none " +
          (isSelected ? "drop-shadow-[0_0_4px_var(--pi-game-blue)] " : "") +
          (isDropTarget ? "ring-[3px] ring-white " : "")
        }
        data-testid={`image-dnd-zone-${dropZone.id}`}
        key={dropZone.id}
        onDragLeave={() => onZoneDragLeave(dropZone)}
        onDragOver={(event) => onZoneDragOver(event, dropZone)}
        onDrop={(event) => onZoneDrop(event, dropZone)}
        onPointerDown={(event) => onZonePointerDown(event, effectiveZone, "move")}
        style={{
          left: `${effectiveZone.xPos * 100}%`,
          top: `${effectiveZone.yPos * 100}%`,
          width: `${effectiveZone.xSize * 100}%`,
          height: `${effectiveZone.ySize * 100}%`,
          transform: "translate(-50%, -50%)",
          backgroundColor: "rgba(81, 130, 158, 0.28)",
        }}
      >
        <div
          className="absolute left-0 top-0 h-[4px] w-full rounded-t-[5px] bg-[repeating-linear-gradient(90deg,#51829E_0px,#51829E_8px,transparent_8px,transparent_14px)]"
          data-testid={`image-dnd-zone-border-top-${dropZone.id}`}
        />
        <div className="absolute left-0 bottom-0 h-[4px] w-full rounded-b-[5px] bg-[repeating-linear-gradient(90deg,#51829E_0px,#51829E_8px,transparent_8px,transparent_14px)]" />
        <div className="absolute left-0 top-0 h-full w-[4px] rounded-l-[5px] bg-[repeating-linear-gradient(180deg,#51829E_0px,#51829E_8px,transparent_8px,transparent_14px)]" />
        <div className="absolute right-0 top-0 h-full w-[4px] rounded-r-[5px] bg-[repeating-linear-gradient(180deg,#51829E_0px,#51829E_8px,transparent_8px,transparent_14px)]" />
        <div
          className="absolute left-[6px] top-[6px] z-[2] flex items-center justify-center min-w-[28px] h-[28px] px-[8px] text-[13px] font-semibold text-white bg-[rgba(0,0,0,0.25)] rounded-[4px]"
          data-testid={`image-dnd-zone-letter-${dropZone.id}`}
        >
          {zoneLetter}
        </div>
        {image?.assetId && (
          <AssignedZoneImage
            assetId={image.assetId}
            onAspectRatioChange={(aspectRatio) =>
              onImageAspectRatioChange(image.id, aspectRatio)
            }
            zoneId={dropZone.id}
          />
        )}
        <div
          aria-label={`Resize zone ${index + 1}`}
          className="absolute z-[2] right-[-7px] bottom-[-7px] w-[18px] h-[18px] cursor-nwse-resize bg-white rounded-full border-[2px] border-interaction-primary shadow-[0_1px_4px_rgba(0,0,0,0.18)] touch-none after:content-[''] after:absolute after:right-[4px] after:bottom-[4px] after:w-[6px] after:h-[6px] after:border-r-[2px] after:border-b-[2px] after:border-interaction-primary"
          data-testid={`image-dnd-zone-resize-${dropZone.id}`}
          onPointerDown={(event) => onZonePointerDown(event, effectiveZone, "resize")}
        />
        {isSelected && quiz.dropZones.length > 1 && (
          <button
            aria-label={`Delete drop zone ${zoneLetter}`}
            className="absolute z-[3] right-[-10px] top-[-10px] flex items-center justify-center w-[24px] h-[24px] text-interaction-primary bg-white rounded-full border-[2px] border-interaction-primary shadow-[0_1px_4px_rgba(0,0,0,0.18)] hover:bg-interaction-primary-washed"
            data-testid={`image-dnd-zone-delete-${dropZone.id}`}
            onClick={(event) => {
              event.stopPropagation();
              deleteDropZone(dropZone.id);
            }}
            onPointerDown={(event) => event.stopPropagation()}
            type="button"
          >
            <CloseIcon fontSize="inherit" />
          </button>
        )}
      </div>
    );
  };

  const imagePickerButton = (
    <button
      aria-label={quiz.imageAssetId ? "Change image" : "Select image"}
      className="absolute flex right-[16px] bottom-[16px] bg-interaction-primary text-white text-[28px] w-[44px] h-[44px] rounded-full cursor-pointer hover:opacity-75"
      onClick={() => setFileExplorerOpen(true)}
      type="button"
    >
      {quiz.imageAssetId ? (
        <ImageSearchOutlinedIcon className="m-auto" fontSize="inherit" />
      ) : (
        <AddPhotoAlternateOutlinedIcon className="m-auto" fontSize="inherit" />
      )}
    </button>
  );

  const imageOverlayStyle: CSSProperties = imageOverlayBounds
    ? {
        left: `${imageOverlayBounds.left}px`,
        top: `${imageOverlayBounds.top}px`,
        width: `${imageOverlayBounds.width}px`,
        height: `${imageOverlayBounds.height}px`,
      }
    : {
        left: 0,
        top: 0,
        width: "100%",
        height: "100%",
      };

  return (
    <>
      <div className="imagedndquizpreview-container">
        <div className="imagedndquizpreview-root">
          <div className="imagedndquizpreview-stage-panel relative flex min-h-0 bg-white dark:bg-creator-surface rounded-[5px] overflow-hidden dark:border dark:border-solid dark:border-creator-border">
            {!quiz.imageAssetId && (
              <button
                className="flex flex-col items-center justify-center w-full h-full gap-[12px] text-interaction-primary hover:bg-interaction-primary-washed"
                data-testid="image-dnd-background-empty"
                onClick={() => setFileExplorerOpen(true)}
                type="button"
              >
                <AddPhotoAlternateOutlinedIcon fontSize="large" />
                <span className="text-[16px] font-semibold">Select image</span>
              </button>
            )}
            {quiz.imageAssetId && (
              <div
                className="relative flex items-center justify-center w-full h-full p-[16px]"
                data-testid="image-dnd-stage"
                ref={imageStageRef}
              >
                <img
                  alt="Image Drag And Drop Quiz"
                  className="block max-w-full max-h-full object-contain"
                  onLoad={onBackgroundImageLoad}
                  ref={backgroundImageRef}
                  src={previewUrl}
                />
                <div
                  className="absolute"
                  data-testid="image-dnd-zone-overlay"
                  onPointerMove={onPointerMove}
                  onPointerUp={onPointerUp}
                  ref={imageOverlayRef}
                  style={imageOverlayStyle}
                >
                  {quiz.dropZones.map(renderZone)}
                </div>
              </div>
            )}
            {quiz.imageAssetId && (
              <div className="absolute left-[16px] bottom-[16px] z-[3] flex gap-[8px]">
                <button
                  aria-label="Add drop zone"
                  className="flex items-center justify-center w-[44px] h-[44px] text-[28px] font-semibold text-white bg-interaction-primary rounded-full hover:bg-interaction-primary-hover disabled:bg-interaction-disabled"
                  disabled={quiz.dropZones.length >= MAX_ZONES}
                  onClick={addDropZone}
                  title="Add drop zone"
                  type="button"
                >
                  +
                </button>
              </div>
            )}
            {imagePickerButton}
          </div>
          <div className="imagedndquizpreview-answer-panel flex flex-col min-h-0 gap-[12px]">
            <Section
              className="imagedndquizpreview-answer-section"
              bodyClassName="imagedndquizpreview-answer-section-body"
              title={`Images (${quiz.images.length} / ${IMAGE_DRAG_AND_DROP_QUIZ_MAX_IMAGES})`}
            >
              <div className="imagedndquizpreview-answer-list pit-scrollbar flex flex-1 min-h-[140px] gap-[10px] pr-[4px] py-[2px]">
                {quiz.images.map(renderImage)}
                {canAddImage && (
                  <div className="imagedndquizpreview-inline-add-image">
                    <PiBasicQuizAddAnswerButtonV2
                      onClick={addImage}
                      text="Add image"
                    />
                  </div>
                )}
              </div>
            </Section>
          </div>
        </div>
      </div>

      <FileExplorerDialog
        open={fileExplorerOpen}
        onClose={() => setFileExplorerOpen(false)}
        onSelect={onSelectImage}
        type={AssetKey.Image}
      />
    </>
  );
}
